import { Dispatch, useEffect, useRef } from "react";
import { getLocalJWT, getLocalUserId } from "../functions/utilities";
import { socketInit } from "../Hooks/socketInit";
import {
  PULSE_BANK_STATEMENT_DATA_TYPE,
  PULSE_DEADLINE_SUBMITTED_SOCKET_EVENT,
} from "../../constants/pulse.constants";
import { Action } from "./reducer";
import {
  PulseAccountSocketPayload,
  PulseDeadlineSubmittedSocketPayload,
  PulseIdentificationSocketPayload,
  PulseOcrSocketPayload,
  PulseReconciliationFailedSocketPayload,
  PulseReconciliationSocketPayload,
  PulseUploadAcceptedSocketPayload,
} from "./types";

export function useBankStatementsSocket(
  dispatch: Dispatch<Action>,
  refresh: () => Promise<void>,
  isPulseMonitoring = false,
  onActionCompleted?: () => void
) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const onActionCompletedRef = useRef(onActionCompleted);
  onActionCompletedRef.current = onActionCompleted;
  const completedScheduleIdsRef = useRef(new Set<number>());

  useEffect(() => {
    const userId = getLocalUserId();
    const token = getLocalJWT();
    if (!userId || !token) return;

    let socket: ReturnType<typeof socketInit>;
    try {
      socket = socketInit(userId.toString(), token);
    } catch (_error) {
      void refreshRef.current();
      return;
    }

    let hasDisconnected = false;

    const joinUserRoom = () => {
      socket.emit("JOIN_USER_ROOM", { roomId: `user_${userId}` });
    };

    const handleConnect = () => {
      joinUserRoom();
      if (hasDisconnected) void refreshRef.current();
    };
    const handleDisconnect = () => {
      hasDisconnected = true;
    };
    const handleConnectError = () => {
      hasDisconnected = true;
    };
    const handleIdentification = (payload: PulseIdentificationSocketPayload) => {
      if (!payload) return;
      dispatch({ type: "IDENTIFICATION_RESULT", payload });
      if (isPulseMonitoring) void refreshRef.current();
    };
    const handleIdentificationStarted = (payload: PulseAccountSocketPayload & { docIds?: number[] }) => {
      if (!payload) return;
      dispatch({
        type: "IDENTIFICATION_STARTED",
        docIds: payload?.docIds || [],
      });
      dispatch({
        type: "ACCOUNT_PROCESSING",
        payload,
        status: "identifying",
      });
    };
    const handleProcessing = (payload: PulseAccountSocketPayload) => {
      if (!payload) return;
      dispatch({ type: "ACCOUNT_PROCESSING", payload, status: "processing" });
    };
    const handleOcrComplete = (payload: PulseOcrSocketPayload) => {
      if (!payload) return;
      dispatch({ type: "OCR_COMPLETE", payload });
    };
    const handleFailed = (payload: PulseAccountSocketPayload) => {
      if (!payload) return;
      dispatch({ type: "ACCOUNT_PROCESSING", payload, status: "failed" });
    };
    const handleReconciliation = (payload: PulseReconciliationSocketPayload) => {
      if (!payload) return;
      dispatch({
        type: "RECONCILIATION_COMPLETE",
        payload,
        updatedAt: new Date().toISOString(),
      });
      // The event wipes errorDetails locally; re-fetch so a consent-overridden
      // job gets its ranges and Undo back.
      void refreshRef.current();
    };
    const handleReconciliationFailed = (
      payload: PulseReconciliationFailedSocketPayload
    ) => {
      if (!payload) return;
      dispatch({
        type: "RECONCILIATION_FAILED",
        payload,
        updatedAt: new Date().toISOString(),
      });
      void refreshRef.current();
    };
    const handleUploadAccepted = (_payload: PulseUploadAcceptedSocketPayload) => {
    };
    const handleDeadlineSubmitted = (
      payload: PulseDeadlineSubmittedSocketPayload
    ) => {
      if (
        !isPulseMonitoring ||
        !payload ||
        payload.dataType !== PULSE_BANK_STATEMENT_DATA_TYPE
      ) {
        return;
      }
      dispatch({ type: "DEADLINE_SUBMITTED", payload });
      if (completedScheduleIdsRef.current.has(payload.scheduleId)) return;

      completedScheduleIdsRef.current.add(payload.scheduleId);
      onActionCompletedRef.current?.();
    };

    const subscriptions: Array<[string, (payload: any) => void]> = [
      ["connect", handleConnect],
      ["disconnect", handleDisconnect],
      ["connect_error", handleConnectError],
      ["PULSE_UPLOAD_ACCEPTED", handleUploadAccepted],
      ["PULSE_IDENTIFICATION_STARTED", handleIdentificationStarted],
      ["PULSE_ACCOUNT_RESOLVED", handleIdentification],
      ["PULSE_IDENTIFICATION_FAILED", handleIdentification],
      ["PULSE_OCR_UPDATE", handleProcessing],
      ["PULSE_OCR_COMPLETE", handleOcrComplete],
      ["PULSE_CONSOLIDATION_STARTED", handleProcessing],
      ["PULSE_CONSOLIDATION_COMPLETE", handleProcessing],
      ["PULSE_CONSOLIDATION_FAILED", handleFailed],
      ["PULSE_RECONCILIATION_COMPLETE", handleReconciliation],
      ["PULSE_RECONCILIATION_FAILED", handleReconciliationFailed],
      [PULSE_DEADLINE_SUBMITTED_SOCKET_EVENT, handleDeadlineSubmitted],
    ];

    subscriptions.forEach(([event, handler]) => socket.on(event, handler));

    if (socket.connected) joinUserRoom();

    return () => {
      subscriptions.forEach(([event, handler]) => socket.off(event, handler));
      socket.disconnect();
    };
  }, [dispatch, isPulseMonitoring]);
}
